import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import embeddingService, {
  ClusteringProject, ClusteringProjectDetail, ClusteringRunItem,
  EmbeddingStatus, ImageSearchItem,
} from '../services/embeddingService'
import ClusterDistributionPanel, { PreviewImage } from '../components/ClusterDistributionPanel'
import CreateClusteringProjectModal from '../components/CreateClusteringProjectModal'
import ClusteringSourcePickerModal from '../components/ClusteringSourcePickerModal'
import ClusterDownloadModal from '../components/ClusterDownloadModal'
import ImageResultCard from '../components/ImageResultCard'
import { formatLocaleDatetime } from '../utils/date'

type Tab = 'clustering' | 'search'

const RUN_STATUS: Record<string, { label: string; className: string; dot: string }> = {
  queued: { label: '대기 중', className: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
  running: { label: '진행 중', className: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500' },
  completed: { label: '완료', className: 'bg-green-100 text-green-800', dot: 'bg-green-500' },
  failed: { label: '실패', className: 'bg-red-100 text-red-800', dot: 'bg-red-500' },
}

const count = (value: number) => value.toLocaleString()

/** 관련도 하한 기본값(%) — 1위 유사도 대비 비율이다.
 *
 * 절대 유사도로는 자를 수 없다. 텍스트와 이미지의 코사인 유사도는 질의문에 따라
 * 통째로 움직인다 (실측: 「사람이 있는 사진」 1위 0.328 / 「비 오는 야간 장면」 1위
 * 0.660). 절대 60% 로 자르면 어떤 질의든 결과가 0개였다.
 * 1위 대비 95% 면 질의별로 7~160개가 남는다 (같은 실측).
 */
const SEARCH_MIN_RELATIVE = 95
/** 한 번의 검색에서 훑을 최대 개수. 하한을 넘는 게 이보다 많으면 잘렸다고 알린다. */
const SEARCH_SCAN_LIMIT = 500
/** 검색결과 한 페이지에 보여줄 장수. */
const SEARCH_PAGE_SIZE = 60

/** 이미지 탐색.
 *
 * 클러스터링은 프로젝트 안에서 사람이 대상 폴더를 골라 만든다. 예전처럼 전체
 * 이미지를 자동으로 다시 나누지 않는다 — 그러면 재실행마다 관리자가 붙인
 * 클러스터 이름이 사라진다.
 *
 * 레이아웃은 다른 관리 페이지(학습·평가 데이터셋, 라벨 데이터)와 같은 틀이다 —
 * 좌측 고정폭 구조 트리 + 우측 상세가 테두리 하나 안에 들어간다. 색만 맞추고
 * 구조를 다르게 두면 같은 제품에서 페이지마다 조작법이 달라진다.
 */
export default function ImageExplorer({ userInfo }: { userInfo: { isAdmin: boolean } }) {
  const [tab, setTab] = useState<Tab>('clustering')
  const [projects, setProjects] = useState<ClusteringProject[]>([])
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  // 프로젝트 상세를 하나만 들고 있으면, 펼쳐둔 프로젝트라도 다른 것을 여는 순간
  // 하위 클러스터가 사라지고 「클릭하여 열기」로 되돌아간다. 프로젝트별로 보관해
  // 한 번 펼친 것은 계속 펼쳐진 채로 둔다 (펼침 자체는 다른 구조 트리와 같이
  // 접힌 상태에서 시작해 사용자가 누른 것만 펼친다).
  const [details, setDetails] = useState<Record<number, ClusteringProjectDetail>>({})
  const [openProjectId, setOpenProjectId] = useState<number | null>(null)
  const openProject = openProjectId != null ? details[openProjectId] ?? null : null
  const [openRun, setOpenRun] = useState<ClusteringRunItem | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // 전체화면은 좌측 「클러스터링 구조」를 뺀 우측 영역을 키운다. 3D 칸만 키우면
  // 좌표는 커지는데 클러스터 상세를 같이 볼 수 없다.
  const resultPaneRef = useRef<HTMLDivElement>(null)
  const [paneFullscreen, setPaneFullscreen] = useState(false)

  const [statusInfo, setStatusInfo] = useState<EmbeddingStatus | null>(null)
  const [statusOpen, setStatusOpen] = useState(false)
  const autoOpened = useRef(false)

  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState<ImageSearchItem[]>([])
  // 관련도 하한(%) — 상위 N개를 자르는 대신 이 값을 넘는 것만 받는다. 찾는 장면이
  // 많으면 결과도 많아야 하고, 없으면 억지로 채우지 않는 게 맞다.
  const [minRelative, setMinRelative] = useState(SEARCH_MIN_RELATIVE)
  const [searchTruncated, setSearchTruncated] = useState(false)
  // 이번 질의의 1위 유사도 — 결과 카드의 관련도 기준선이다.
  const [topSimilarity, setTopSimilarity] = useState(0)
  const [searchPage, setSearchPage] = useState(0)
  const [downloadOpen, setDownloadOpen] = useState(false)
  // 확대창에는 파일 id 와 (있으면) 파일명만 필요하다. 대표·경계 썸네일은 파일명이 없다.
  const [preview, setPreview] = useState<PreviewImage | null>(null)

  const canManage = userInfo.isAdmin

  const loadStatus = useCallback(async () => {
    try {
      const info = await embeddingService.status()
      setStatusInfo(info)
      if (!autoOpened.current) {
        if (!info.is_complete || info.source_missing > 0 || info.other_failed > 0) setStatusOpen(true)
        autoOpened.current = true
      }
    } catch (e: any) { setError(e?.message || '상태 조회 실패') }
  }, [])

  const loadProjects = useCallback(async () => {
    setLoading(true)
    try {
      const list = await embeddingService.clusteringProjects()
      setProjects(list)
      const loaded = await Promise.all(list.map(async item => {
        try { return await embeddingService.clusteringProject(item.id) }
        catch { return null }
      }))
      setDetails(prev => {
        const next: Record<number, ClusteringProjectDetail> = {}
        list.forEach(item => { if (prev[item.id]) next[item.id] = prev[item.id] })
        loaded.forEach(detail => { if (detail) next[detail.id] = detail })
        return next
      })
    }
    catch (e: any) { setError(e?.message || '프로젝트 조회 실패') }
    finally { setLoading(false) }
  }, [])

  const loadProject = useCallback(async (projectId: number) => {
    try {
      const detail = await embeddingService.clusteringProject(projectId)
      setDetails(prev => ({ ...prev, [projectId]: detail }))
      return detail
    } catch (e: any) { setError(e?.message || '프로젝트 조회 실패') }
    return null
  }, [])

  useEffect(() => {
    void loadStatus()
    void loadProjects()
    const timer = window.setInterval(loadStatus, 30_000)
    return () => window.clearInterval(timer)
  }, [loadStatus, loadProjects])

  // 진행 중인 클러스터링이 있으면 끝날 때까지 지켜본다. 트리에 모든 프로젝트가
  // 펼쳐져 있으므로 열어둔 하나가 아니라 진행 중인 것을 전부 갱신한다.
  const pendingIds = Object.values(details)
    .filter(detail => detail.runs.some(
      run => run.status === 'queued' || run.status === 'running'))
    .map(detail => detail.id)
  const pendingKey = pendingIds.join(',')
  useEffect(() => {
    if (!pendingKey) return
    const timer = window.setInterval(() => {
      pendingKey.split(',').forEach(id => { void loadProject(Number(id)) })
      void loadProjects()
    }, 5_000)
    return () => window.clearInterval(timer)
  }, [pendingKey, loadProject, loadProjects])

  const selectProject = async (projectId: number) => {
    setExpanded(prev => new Set(prev).add(projectId))
    setOpenRun(null)
    setOpenProjectId(projectId)
    await loadProject(projectId)
  }

  const toggleProject = (projectId: number) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(projectId)) next.delete(projectId)
      else next.add(projectId)
      return next
    })
  }

  const createProject = async (name: string, description: string) => {
    const created = await embeddingService.createClusteringProject(name, description)
    setCreateOpen(false)
    await loadProjects()
    await selectProject(created.id)
  }

  const createRun = async (nodeIds: number[]) => {
    if (!openProject) return
    await embeddingService.createClusteringRun(openProject.id, nodeIds)
    setPickerOpen(false)
    await loadProject(openProject.id)
    await loadProjects()
  }

  const removeProject = async (project: ClusteringProject) => {
    if (!window.confirm(`'${project.name}' 프로젝트와 그 안의 클러스터링 결과를 모두 삭제합니다.`)) return
    try {
      await embeddingService.deleteClusteringProject(project.id)
      if (openProjectId === project.id) { setOpenProjectId(null); setOpenRun(null) }
      setDetails(prev => {
        const next = { ...prev }
        delete next[project.id]
        return next
      })
      await loadProjects()
    } catch (e: any) { setError(e?.message || '삭제 실패') }
  }

  const removeRun = async (run: ClusteringRunItem) => {
    if (!window.confirm('이 클러스터링 결과를 삭제합니다. 붙여둔 클러스터 이름도 함께 사라집니다.')) return
    try {
      await embeddingService.deleteClusteringRun(run.id)
      if (openRun?.id === run.id) setOpenRun(null)
      if (openProject) await loadProject(openProject.id)
      await loadProjects()
    } catch (e: any) { setError(e?.message || '삭제 실패') }
  }

  const submitSearch = async (event: FormEvent) => {
    event.preventDefault()
    const value = query.trim()
    if (!value || searching) return
    setSearching(true); setError('')
    try {
      const page = await embeddingService.search(
        value, SEARCH_SCAN_LIMIT, undefined, minRelative / 100)
      setResults(page.items)
      setSearchTruncated(page.truncated)
      setTopSimilarity(page.top_similarity)
      setSearchPage(0)
    }
    catch (e: any) { setError(e?.message || '검색 실패') }
    finally { setSearching(false) }
  }

  const downloadResults = async (filename: string) => {
    const ids = results.map(item => item.id)
    let snapshot: string | undefined
    // 패키징이 필요한 규모면 202 가 온다. 완료될 때까지 기다린 뒤 같은 스냅샷으로 받는다.
    for (let attempt = 0; attempt < 600; attempt += 1) {
      const outcome = await embeddingService.downloadSearchResults(ids, filename, snapshot)
      if (outcome.status === 'ok') { setDownloadOpen(false); return }
      snapshot = outcome.snapshot || snapshot
      await new Promise(resolve => window.setTimeout(resolve, 3_000))
    }
    throw new Error('패키징이 오래 걸립니다. 잠시 후 다시 시도해주세요.')
  }

  useEffect(() => {
    const sync = () => setPaneFullscreen(document.fullscreenElement === resultPaneRef.current)
    document.addEventListener('fullscreenchange', sync)
    return () => document.removeEventListener('fullscreenchange', sync)
  }, [])

  const togglePaneFullscreen = () => {
    const el = resultPaneRef.current
    if (!el) return
    if (document.fullscreenElement) void document.exitFullscreen()
    else void el.requestFullscreen?.()
  }

  const tabClass = (active: boolean) =>
    `px-3 py-1 text-sm rounded ${active ? 'bg-gray-800 text-white' : 'border border-gray-300 bg-white hover:bg-gray-50'}`

  const runLabel = (run: ClusteringRunItem) =>
    run.source_labels.length === 1
      ? run.source_labels[0].split(' / ').slice(-1)[0]
      : `폴더 ${count(run.source_labels.length)}개`

  return (
    <div className="h-full flex flex-col">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">이미지 탐색</h1>
        <p className="text-gray-600">
          원천데이터를 선택하여 데이터 분포를 분석하고, 자연어로 이미지를 검색합니다.
        </p>
      </div>

      {statusInfo && (
        <div className="mb-3 border border-gray-200 rounded-lg bg-white px-4 py-2">
          <button
            type="button"
            onClick={() => setStatusOpen(v => !v)}
            className="flex w-full items-center gap-3 text-left"
          >
            <span className={`h-2 w-2 shrink-0 rounded-full ${
              statusInfo.total_images === 0 ? 'bg-gray-300'
                : statusInfo.is_complete ? 'bg-green-500' : 'bg-blue-500'}`} />
            <span className="text-sm font-semibold text-gray-800">
              {statusInfo.total_images === 0 ? '데이터 없음'
                : statusInfo.is_complete ? '이미지 분석 완료' : '이미지 분석 중'}
            </span>
            <span className="text-xs text-gray-500">
              {count(statusInfo.embedded)} / {count(statusInfo.total_images)}건
            </span>
            {(statusInfo.source_missing > 0 || statusInfo.other_failed > 0) && (
              <span className="px-2 py-0.5 rounded bg-amber-50 text-[11px] font-medium text-amber-700">
                제외 {count(statusInfo.source_missing + statusInfo.other_failed)}건
              </span>
            )}
            <svg
              className={`ml-auto w-4 h-4 shrink-0 text-gray-400 transition-transform ${statusOpen ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {statusOpen && (
            <div className="mt-2 border-t border-gray-100 pt-2">
              {statusInfo.pending > 0 && (
                <div className="mb-2 border border-blue-200 bg-blue-50 rounded px-3 py-2 text-xs font-medium text-blue-700">
                  이미지 분석을 자동으로 진행하고 있습니다. 분석이 끝난 폴더만 클러스터링에 사용할 수 있습니다.
                </div>
              )}
              {statusInfo.source_missing > 0 && (
                <div className="mb-2 border border-amber-200 bg-amber-50 rounded px-3 py-2 text-xs font-medium text-amber-700">
                  작업파일이 없는 {count(statusInfo.source_missing)}건은 대상에서 제외됩니다. 나머지 데이터 사용에는 영향이 없습니다.
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                <div className="rounded bg-green-50 px-3 py-2 text-green-800">정상 완료 <strong>{count(statusInfo.embedded)}</strong>건</div>
                <div className="rounded bg-blue-50 px-3 py-2 text-blue-700">처리 대기 <strong>{count(statusInfo.pending)}</strong>건</div>
                <div className="rounded bg-amber-50 px-3 py-2 text-amber-700">작업파일 누락 <strong>{count(statusInfo.source_missing)}</strong>건</div>
                <div className="rounded bg-red-50 px-3 py-2 text-red-700">기타 실패 <strong>{count(statusInfo.other_failed)}</strong>건</div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mb-3 flex items-center gap-2">
        {([['clustering', '클러스터링'], ['search', '자연어 검색']] as [Tab, string][]).map(([key, label]) => (
          <button key={key} type="button" onClick={() => setTab(key)} className={tabClass(tab === key)}>
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-3 flex items-center gap-2 border border-red-200 bg-red-50 rounded px-4 py-2 text-sm text-red-600">
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-400 hover:text-red-600">닫기</button>
        </div>
      )}

      {tab === 'search' ? (
        <div
          className="flex flex-col border border-gray-200 rounded-lg bg-white p-4"
          style={{ height: 'calc(100vh - 270px)' }}
        >
          <form onSubmit={submitSearch} className="mb-4 flex gap-2">
            <input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="예: 낮에 촬영된 도로 이미지, 비 오는 야간 장면"
              maxLength={500}
              className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
            />
            <label className="flex items-center gap-1.5 text-sm text-gray-600 whitespace-nowrap">
              관련도
              <input
                type="number"
                value={minRelative}
                onChange={event => setMinRelative(Math.min(100, Math.max(0,
                  Number(event.target.value) || 0)))}
                min={0}
                max={100}
                className="w-16 border border-gray-300 rounded px-2 py-2 text-sm text-right"
              />
              % 이상
            </label>
            <button
              type="submit"
              disabled={searching || !query.trim()}
              className={`px-4 py-2 text-sm rounded text-white ${
                searching || !query.trim() ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-800 hover:bg-gray-900'
              }`}
            >{searching ? '검색 중...' : '검색'}</button>
          </form>
          {results.length > 0 ? (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-sm text-gray-500">
                  관련도 {minRelative}% 이상 {count(results.length)}개
                  {searchTruncated && (
                    <span className="ml-1.5 text-amber-700">
                      (상한 {count(SEARCH_SCAN_LIMIT)}개까지만 확인 — 더 있을 수 있습니다)
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => setDownloadOpen(true)}
                  className="ml-auto px-3 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
                >검색결과 다운로드</button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
                  {results
                    .slice(searchPage * SEARCH_PAGE_SIZE, (searchPage + 1) * SEARCH_PAGE_SIZE)
                    .map(item => (
                      <ImageResultCard
                        key={item.id} item={item} onOpen={setPreview} compact
                        baseline={topSimilarity}
                      />
                    ))}
                </div>
              </div>
              {results.length > SEARCH_PAGE_SIZE && (
                <div className="mt-3 flex items-center justify-center gap-2 border-t border-gray-100 pt-3">
                  <button
                    type="button"
                    onClick={() => setSearchPage(page => Math.max(0, page - 1))}
                    disabled={searchPage === 0}
                    className={`px-3 py-1 text-xs rounded border ${
                      searchPage === 0
                        ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                        : 'border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                  >이전</button>
                  <span className="text-xs text-gray-500">
                    {searchPage + 1} / {Math.ceil(results.length / SEARCH_PAGE_SIZE)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSearchPage(page => Math.min(
                      Math.ceil(results.length / SEARCH_PAGE_SIZE) - 1, page + 1))}
                    disabled={(searchPage + 1) * SEARCH_PAGE_SIZE >= results.length}
                    className={`px-3 py-1 text-xs rounded border ${
                      (searchPage + 1) * SEARCH_PAGE_SIZE >= results.length
                        ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                        : 'border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                  >다음</button>
                </div>
              )}
            </>
          ) : !searching && (
            <div className="flex flex-1 items-center justify-center text-gray-500">
              <p className="text-sm">
                {query.trim()
                  ? `관련도 ${minRelative}% 이상인 이미지가 없습니다. 하한을 낮춰보세요.`
                  : '찾고 싶은 장면을 자연어로 입력하세요.'}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div
          className="flex border border-gray-200 rounded-lg overflow-hidden bg-white"
          /* Layout 이 minHeight 만 주므로 h-full·flex-1 로는 높이가 확정되지 않는다.
             여기서 못박지 않으면 우측 이미지 그리드가 길어지는 만큼 좌측 3D 캔버스도
             같이 늘어나 화면 밖으로 나간다. 다른 페이지도 calc 로 고정한다. */
          style={{ height: 'calc(100vh - 270px)' }}
        >
          {/* ── 좌측: 클러스터링 구조 (프로젝트 > 클러스터링) ── */}
          <div className="w-80 flex-none border-r border-gray-200 bg-gray-50 flex flex-col">
            <div className="p-4 min-h-16 border-b border-gray-200 bg-white flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">클러스터링 구조</h3>
              {canManage && (
                <button
                  onClick={() => setCreateOpen(true)}
                  className="px-3 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-900"
                >+ 프로젝트 생성</button>
              )}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-2">
              {loading ? (
                <div className="p-4 text-sm text-gray-500">로딩 중...</div>
              ) : projects.length === 0 ? (
                <div className="flex h-full items-center justify-center">
                  <div className="text-center text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <p className="text-sm mb-2">등록된 클러스터링 프로젝트가 없습니다</p>
                    <p className="text-xs text-gray-400 mb-3">
                      {canManage ? '새로운 프로젝트를 생성해주세요' : '관리자에게 프로젝트 생성을 요청하세요.'}
                    </p>
                    {canManage && (
                      <button
                        onClick={() => setCreateOpen(true)}
                        className="px-3 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-900"
                      >프로젝트 생성</button>
                    )}
                  </div>
                </div>
              ) : (
                projects.map(project => {
                  const isOpen = expanded.has(project.id)
                  const isSelected = openProjectId === project.id && !openRun
                  const detail = details[project.id]
                  const runs = detail?.runs ?? []
                  return (
                    <div key={project.id}>
                      <div
                        onClick={() => void selectProject(project.id)}
                        className={`flex items-center py-2 px-2 cursor-pointer rounded transition-colors ${
                          isSelected ? 'bg-gray-100 border-l-4 border-gray-800 text-gray-900' : 'hover:bg-gray-100'
                        }`}
                      >
                        {/* 폴더 아이콘 → 화살표 → 이름 (개수) 순서와 표기는 다른 구조
                            트리(데이터셋·라벨·원천·학습이력)와 같아야 한다 */}
                        <svg className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                        {runs.length > 0 && (
                          <button
                            onClick={e => { e.stopPropagation(); toggleProject(project.id) }}
                            className="mr-1 w-4 h-4 flex-shrink-0 flex items-center justify-center"
                            aria-label={isOpen ? '접기' : '펼치기'}
                          >
                            <svg
                              className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        )}
                        <span className="text-sm font-medium text-gray-900 min-w-0 break-all">
                          {project.name}{runs.length > 0 ? ` (${runs.length})` : ''}
                        </span>
                        {project.running_count > 0 && (
                          <span className="ml-1.5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" title="진행 중" />
                        )}
                      </div>

                      {/* 자식이 없으면 하위 영역도 그리지 않는다 — 다른 구조 트리와 같다.
                          클러스터링이 없다는 안내는 우측 상세가 맡는다 */}
                      {isOpen && runs.length > 0 && (
                        <div className="ml-6">
                          {runs.map(run => {
                            const badge = RUN_STATUS[run.status] || RUN_STATUS.queued
                            const selected = openRun?.id === run.id
                            return (
                              <div
                                key={run.id}
                                onClick={() => {
                                  if (run.status !== 'completed') return
                                  // 트리에 모든 프로젝트가 펼쳐져 있으므로 프로젝트를
                                  // 거치지 않고 클러스터를 바로 누를 수 있다. 우측 상세는
                                  // 어느 프로젝트의 것인지까지 있어야 그린다.
                                  setOpenProjectId(project.id)
                                  setOpenRun(run)
                                }}
                                className={`flex items-center py-1.5 px-2 rounded transition-colors ${
                                  run.status === 'completed' ? 'cursor-pointer' : 'cursor-default'
                                } ${
                                  selected ? 'bg-gray-100 border-l-4 border-gray-800' : 'hover:bg-gray-100'
                                }`}
                              >
                                <span className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 ${badge.dot}`} />
                                <span className={`text-sm font-medium min-w-0 break-all ${
                                  run.status === 'completed' ? 'text-gray-900' : 'text-gray-500'
                                }`}>
                                  {runLabel(run)}
                                  {run.status === 'completed' ? ` (${count(run.leaf_count)})` : ''}
                                </span>
                                {run.status !== 'completed' && (
                                  <span className="ml-1.5 flex-shrink-0 text-xs text-gray-400">{badge.label}</span>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </div>

          {/* ── 우측: 선택한 프로젝트 / 클러스터링 상세 ── */}
          <div
            ref={resultPaneRef}
            className={`flex min-h-0 min-w-0 flex-1 flex-col ${paneFullscreen ? 'bg-white' : ''}`}
          >
            {!openProject ? (
              <div className="flex flex-1 items-center justify-center text-gray-500">
                <div className="text-center">
                  <p className="text-lg font-medium">프로젝트를 선택하세요</p>
                  <p className="mt-1 text-sm text-gray-400">
                    좌측에서 클러스터링 프로젝트를 고르면 결과가 여기에 나옵니다.
                  </p>
                </div>
              </div>
            ) : !openRun ? (
              <>
                <div className="p-4 min-h-16 border-b border-gray-200 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{openProject.name}</h3>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {openProject.description || '설명 없음'}
                    </p>
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    {canManage && (
                      <>
                        <button
                          onClick={() => setPickerOpen(true)}
                          className="px-3 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-900"
                        >+ 클러스터링 생성</button>
                        <button
                          onClick={() => {
                            const p = projects.find(x => x.id === openProject.id)
                            if (p) void removeProject(p)
                          }}
                          className="px-3 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50"
                        >프로젝트 삭제</button>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto p-4">
                  {openProject.runs.length === 0 ? (
                    <div className="flex items-center justify-center py-20">
                      <div className="text-center text-gray-500">
                        <p className="text-sm mb-1">아직 만든 클러스터링이 없습니다</p>
                        <p className="text-xs text-gray-400">
                          「클러스터링 생성」에서 원천데이터 폴더를 고르세요.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {openProject.runs.map(run => {
                        const badge = RUN_STATUS[run.status] || RUN_STATUS.queued
                        return (
                          <div
                            key={run.id}
                            onClick={() => {
                              if (run.status !== 'completed') return
                              setOpenRun(run)
                            }}
                            className={`border border-gray-200 rounded p-3 ${
                              run.status === 'completed' ? 'cursor-pointer hover:bg-gray-50' : ''
                            }`}
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badge.className}`}>
                                {badge.label}
                              </span>
                              <span className="text-xs text-gray-400">{formatLocaleDatetime(run.started_at)}</span>
                              {run.status === 'completed' && (
                                <span className="text-xs text-gray-700">
                                  이미지 {count(run.n_samples)}장 · 클러스터 {count(run.leaf_count)}개
                                </span>
                              )}
                              {canManage && run.status !== 'running' && (
                                <button
                                  onClick={e => { e.stopPropagation(); void removeRun(run) }}
                                  className="ml-auto px-2 py-0.5 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50"
                                >삭제</button>
                              )}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1">
                              {run.source_labels.map(label => (
                                <span key={label} className="px-2 py-0.5 rounded bg-gray-100 text-[11px] text-gray-600">
                                  {label}
                                </span>
                              ))}
                            </div>
                            {run.status === 'failed' && run.error && (
                              <div className="mt-2 border border-red-200 bg-red-50 rounded px-3 py-1.5 text-xs text-red-600">
                                {run.error}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="p-4 min-h-16 border-b border-gray-200 flex flex-wrap items-center gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {openProject.name} · {runLabel(openRun)}
                    </h3>
                    <p className="mt-0.5 text-xs text-gray-500">
                      이미지 {count(openRun.n_samples)}장 · 클러스터 {count(openRun.leaf_count)}개 ·{' '}
                      {formatLocaleDatetime(openRun.finished_at || openRun.started_at)}
                    </p>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      type="button"
                      onClick={togglePaneFullscreen}
                      className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
                    >{paneFullscreen ? '닫기' : '전체화면'}</button>
                  </div>
                </div>
                <div className="flex-1 min-h-0">
                  <ClusterDistributionPanel
                    key={`result:${openRun.id}`}
                    runId={openRun.id}
                    isAdmin={userInfo.isAdmin}
                    onOpenPreview={setPreview}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ClusterDownloadModal
        title="검색결과 다운로드"
        isOpen={downloadOpen}
        defaultName={query.trim() ? `search_${query.trim().slice(0, 40)}` : 'search_results'}
        fileCount={results.length}
        onClose={() => setDownloadOpen(false)}
        onSubmit={downloadResults}
      />
      <CreateClusteringProjectModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={createProject}
      />
      <ClusteringSourcePickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSubmit={createRun}
      />

      {preview && createPortal(
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-6"
          onClick={() => setPreview(null)}
        >
          <button
            type="button"
            className="absolute right-6 top-6 px-3 py-1 text-sm border border-white/30 rounded text-white hover:bg-white/10"
            onClick={() => setPreview(null)}
          >닫기</button>
          <div className="max-h-full max-w-6xl" onClick={event => event.stopPropagation()}>
            <img
              src={embeddingService.imageUrl(preview.id)}
              alt={preview.filename || ''}
              className="max-h-[82vh] max-w-full rounded object-contain"
            />
            {preview.filename && (
              <div className="mt-3 text-center text-sm text-white">{preview.filename}</div>
            )}
          </div>
        </div>,
        // 전체화면일 때는 그 요소 안에 붙여야 보인다. 전체화면 요소는 top layer 로
        // 올라가고 그 밖의 DOM 은 아무리 z-index 를 올려도 뒤에 깔린다.
        (paneFullscreen && resultPaneRef.current) || document.body,
      )}
    </div>
  )
}
